/* eslint-disable react-hooks/rules-of-hooks */

import { refreshStoreData } from 'conversations-thread-data/store/public/actions';
import { useEffect, useMemo, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { useChannelConfigurations } from 'conversations-thread-data/channel-configuration/public/hooks';
import { setTracker, setBeaconTracker
//@ts-expect-error no types for this file
} from '../../usage-tracking/clients/usageTracker';
import { useThreadDetails } from '../../thread-details-feature/public/hooks/useThreadDetails';
import { getOriginalGenericChannelId } from 'conversations-thread-data/thread-details/public/operators';
import { visitorUpdated } from 'conversations-thread-data/visitor/actions/visitorUpdated';
import { threadIdChanged } from 'conversations-thread-data/thread-details/public/actions';
//@ts-expect-error no types for this file
import { provideThreadView } from '../decorators/provideThreadView';
import { ticketDisassociated } from 'conversations-thread-data/associations/public/actions';
import { useThreadViewRealtime } from '../../thread-view-realtime/public/hooks/useThreadViewRealtime';
import { getAvailableEditorConfigs } from '../../tabbed-editors/operators/getAvailableEditorConfigs';
//@ts-expect-error no types for this file
import { useThreadDetailsWatcher } from '../hooks/useThreadDetailsWatcher';
import { getUsageTrackingChannel } from 'conversations-thread-data/usage-tracking/public/trackerChannelIdMapping';
import { getInsertCapabilitiesDataMemoized } from 'conversations-thread-data/channel-capabilities/public/hooks';
import Visitor from 'conversations-internal-schema/visitor/records/Visitor';
/**
 * @description This component manages data
 * syncing between the props passed into the thread
 * view and the inner thread-view store. This stuff
 * can't happen in the ThreadViewWrapper, since that isn't
 * connected to the inner store (and shouldn't be, as it's
 * children won't always necessarily be connected to that
 * store either).
 */
export const ThreadViewManager = /*#__PURE__*/forwardRef(({
  inboxId,
  threadChannels,
  threadId,
  ticketCount,
  tracker,
  lifecycle,
  vid,
  visitor
}, ref) => {
  const onThreadTrackingContextChange = lifecycle === null || lifecycle === void 0 ? void 0 : lifecycle.onThreadTrackingContextChange;
  const onThreadDetailsChange = (lifecycle === null || lifecycle === void 0 ? void 0 : lifecycle.onThreadDetailsChange) || (() => {});
  const dispatch = useDispatch();
  const {
    asyncThreadDetails
  } = useThreadDetails({
    threadId
  });
  const threadDetails = getData(asyncThreadDetails);
  const originalGenericChannelId = getOriginalGenericChannelId(threadDetails);
  const {
    asyncChannelConfigurations
  } = useChannelConfigurations();
  const configurations = getData(asyncChannelConfigurations);
  const filteredConfigurations = getAvailableEditorConfigs({
    configurations,
    originalGenericChannelId,
    includeNonGenericChannels: false
  });
  const availableChannels = filteredConfigurations && filteredConfigurations.keySeq().toArray();

  // Optimization to load capablility data early so its already available when used by the editor
  useEffect(() => {
    if (threadId) {
      void getInsertCapabilitiesDataMemoized(threadId);
    }
  }, [threadId]);

  // Wire up Lifecycle Hooks
  useThreadDetailsWatcher({
    threadDetails,
    onChange: onThreadDetailsChange
  });
  useEffect(() => {
    if (onThreadTrackingContextChange && originalGenericChannelId) {
      onThreadTrackingContextChange({
        channel: getUsageTrackingChannel(originalGenericChannelId)
      });
    }
  }, [originalGenericChannelId, onThreadTrackingContextChange]);

  // Core Realtime Setup
  useThreadViewRealtime({
    asyncThreadDetails,
    inboxId,
    threadId,
    threadChannels,
    vid,
    enableThreadPresence: true
  });

  // Imperative API used by the Inbox to hard-refresh this app
  if (ref) {
    ref.current = useMemo(() => ({
      hardRefresh: ({
        threadDetails: nextThreadDetails,
        threadHistory: nextThreadHistory,
        omnibusThreadId
      }) => {
        dispatch(refreshStoreData({
          threadDetails: nextThreadDetails,
          threadHistory: nextThreadHistory,
          threadId: omnibusThreadId
        }));
      },
      ticketDisassociated: ({
        threadId: passedThreadId,
        ticketId
      }) => {
        dispatch(ticketDisassociated({
          threadId: passedThreadId,
          ticketId
        }));
      }
    }), [dispatch]);
  }

  // Tracking Setup and Internal Application events
  useEffect(() => {
    setTracker(tracker, {
      availableChannels,
      threadId,
      ticketCount,
      originalGenericChannelId
    });
  }, [availableChannels, tracker, threadId, originalGenericChannelId, ticketCount]);
  useEffect(() => {
    setBeaconTracker(tracker);
  }, [tracker]);
  useEffect(() => {
    dispatch(threadIdChanged({
      threadId: threadId ? Number(threadId) : null
    }));
  }, [dispatch, threadId]);
  useEffect(() => {
    dispatch(visitorUpdated({
      threadId,
      visitor: visitor || Visitor()
    }));
  }, [dispatch, threadId, visitor]);
  return null;
});
ThreadViewManager.displayName = 'ThreadViewManager';
export default provideThreadView(ThreadViewManager);